import {
  appear,
  appearFromLeft,
  appearFromRight,
  appearFromTop,
} from '@myre/ui-primitive-core';

import { type AnimationEvent } from '@angular/animations';
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  type OnInit,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subject } from 'rxjs';
import { filter } from 'rxjs/operators';

import { type IModalData } from './modal.interface';
import { ModalService } from './modal.service';
import { ModalComponent } from './modal/modal.component';

@Component({
  selector: 'myre-modal-wrapper',
  templateUrl: './modal-wrapper.component.html',
  styleUrls: ['./modal-wrapper.component.scss'],
  animations: [
    appearFromRight(200),
    appearFromLeft(200),
    appearFromTop(200),
    appear(200),
  ],
  imports: [ModalComponent],
})
export class ModalWrapperComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _modalService = inject(ModalService);
  private _changeDetectionRef = inject(ChangeDetectorRef);

  public modals: IModalData[] = [];

  private _showBackgroundChanges = new Subject<boolean>();

  public showBackground: boolean = false;

  public ngOnInit(): void {
    this._modalService.opened
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((modalData: IModalData | null) => modalData !== null),
      )
      .subscribe((modalData: IModalData) => {
        this.showBackground = true;
        this.modals.push(modalData);

        this._changeDetectionRef.detectChanges();
      });

    this._modalService.closed
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        filter((uid: string | null) => uid !== null),
      )
      .subscribe((uid: string) => {
        for (let i = 0; i < this.modals.length; i++) {
          const md = this.modals[i];

          if (md.uid === uid) {
            this.modals.splice(i, 1);

            break;
          }
        }

        const hiddens = this.modals.filter(m => m.hidden);

        if (hiddens.length) {
          this._modalService.show(hiddens[hiddens.length - 1].uid);
        }

        this._changeDetectionRef.detectChanges();
      });

    this._modalService.visibilityChanged
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(() => this._changeDetectionRef.detectChanges());

    this._showBackgroundChanges
      .asObservable()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe(show => {
        this.showBackground = show;
        this._changeDetectionRef.detectChanges();
      });
  }

  public animationDone(): void {
    if (!this.hasModals()) {
      this._showBackgroundChanges.next(false);
    }
  }

  public animationStart(event: AnimationEvent): void {
    if (event.toState === 'show') {
      this._showBackgroundChanges.next(true);
    }
  }

  public hasModals(): boolean {
    return this.modals.length && this.modals.some(m => !m.hidden);
  }

  /** Highest zIndex requested by an open modal, or null for the scss default. */
  public wrapperZIndex(): number | null {
    const requested = this.modals
      .map(m => m.zIndex)
      .filter((z): z is number => typeof z === 'number');

    if (requested.length === 0) {
      return null;
    }

    return Math.max(...requested);
  }

  @HostListener('document:keydown.escape')
  public escape(): void {
    if (!this.modals.length) {
      return;
    }

    this._modalService.close(this.modals.at(-1).uid);
  }
}
